"use client";

import { useEffect } from "react";

/**
 * Browser Real User Monitoring via OpenTelemetry.
 *
 * Opt-in through NEXT_PUBLIC_OTEL_RUM_ENABLED=true (baked at build time).
 * Spans are exported to a SAME-ORIGIN proxy (`/otel/traces`), which injects
 * OTEL_EXPORTER_OTLP_HEADERS and forwards to the real collector server-side —
 * so the auth token and endpoint never reach the browser. Renders nothing.
 */
export function OtelRum() {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_OTEL_RUM_ENABLED !== "true") return;

    let provider: { shutdown: () => Promise<void> } | undefined;

    (async () => {
      const [
        { WebTracerProvider, BatchSpanProcessor },
        { OTLPTraceExporter },
        { ZoneContextManager },
        { registerInstrumentations },
        { FetchInstrumentation },
        { resourceFromAttributes },
      ] = await Promise.all([
        import("@opentelemetry/sdk-trace-web"),
        import("@opentelemetry/exporter-trace-otlp-http"),
        import("@opentelemetry/context-zone"),
        import("@opentelemetry/instrumentation"),
        import("@opentelemetry/instrumentation-fetch"),
        import("@opentelemetry/resources"),
      ]);

      const exporter = new OTLPTraceExporter({
        // Same-origin proxy — no token in the browser, no CORS.
        url: `${window.location.origin}/otel/traces`,
      });

      const webProvider = new WebTracerProvider({
        resource: resourceFromAttributes({
          "service.name":
            process.env.NEXT_PUBLIC_OTEL_RUM_SERVICE_NAME || "rawiah-frontend-rum",
          // Browser env is baked at build time (NEXT_PUBLIC_*) — the browser
          // can't read the server's runtime DEPLOYMENT_ENVIRONMENT. Mirrors the
          // deployment.environment.name the server services set so RUM spans
          // aren't tagged "Not defined".
          "deployment.environment.name":
            process.env.NEXT_PUBLIC_OTEL_RUM_ENVIRONMENT || "development",
        }),
        spanProcessors: [new BatchSpanProcessor(exporter)],
      });
      webProvider.register({ contextManager: new ZoneContextManager() });

      registerInstrumentations({
        tracerProvider: webProvider,
        instrumentations: [
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls: [
              new RegExp(window.location.origin),
            ],
          }),
        ],
      });

      provider = webProvider;
    })();

    return () => {
      provider?.shutdown().catch(() => {});
    };
  }, []);

  return null;
}
